import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Check = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex cursor-pointer gap-3 rounded-2xl border p-4 transition-colors hover:border-primary/40">
    <Checkbox id={id} data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
    <span className="text-sm leading-relaxed">{children}</span>
  </label>
);

export const ConsentGate = () => {
  const { setUser, logout } = useAuth();
  const [age, setAge] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const ref = localStorage.getItem("nmc-ref");
      const { data } = await api.post("/me/consent", { age_or_parent: age, terms, referral_code: ref || null });
      localStorage.removeItem("nmc-ref");
      setUser(data);
      toast.success("Bienvenue sur NoteMyClass AI !");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative flex min-h-screen items-center justify-center px-5 py-10" data-testid="consent-gate">
      <div className="glow left-1/4 top-10 h-80 w-80 bg-blue-400/25" />
      <div className="glass relative w-full max-w-lg rounded-3xl p-8 shadow-xl">
        <Logo />
        <h1 className="mt-6 text-2xl font-extrabold tracking-tight">Avant de commencer</h1>
        <p className="mt-2 text-sm text-muted-foreground">NoteMyClass AI est utilisé par des élèves, dont des mineurs. Merci de confirmer les points suivants.</p>
        <div className="mt-6 space-y-3">
          <Check id="consent-age-checkbox" checked={age} onChange={setAge}>
            J'ai <b>plus de 15 ans</b>, ou j'ai <b>l'accord d'un parent</b> ou représentant légal pour utiliser NoteMyClass AI.
          </Check>
          <Check id="consent-terms-checkbox" checked={terms} onChange={setTerms}>
            J'accepte les <Link to="/confidentialite" target="_blank" className="font-semibold text-primary underline">conditions d'utilisation et la politique de confidentialité</Link>, et je m'engage à n'enregistrer un cours qu'avec l'autorisation du professeur et des personnes concernées.
          </Check>
        </div>
        <p className="mt-5 flex gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> Ta voix et celle des autres ne servent jamais à entraîner une IA. Rien n'est publié.</p>
        <div className="mt-7 flex items-center justify-between gap-3">
          <button data-testid="consent-logout-button" onClick={logout} className="text-sm text-muted-foreground hover:text-foreground">Se déconnecter</button>
          <button data-testid="consent-submit-button" disabled={!age || !terms || busy} onClick={submit}
            className="bg-brand inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accéder à mon espace
          </button>
        </div>
      </div>
    </div>
  );
};
